/**
 * Route access decisions, shared by the router guard (router/index.ts), the
 * permission sync (refresh.ts — "is the open page still allowed?") and links
 * that must not lead to a 403 (notifications, cards).
 *
 *  - `meta.principal` ('staff' | 'client' | 'superadmin') must match the
 *    session's principal — never a role name (R1);
 *  - staff routes need `meta.permission` (slug | {anyOf} | {allOf} |
 *    {slug, scope}), `meta.anyPermission`, or `meta.access: 'self'`; a staff
 *    route with none of them is refused (fail closed);
 *  - routes without a principal (public, verify-email, 403) are open.
 */
import type { RouteLocationNormalized, RouteLocationRaw, RouteMeta } from 'vue-router';
import { meets } from './can';
import { landingRouteName } from './navigation';
import type { PermissionRequirement, PermissionSnapshot, Principal } from './types';

type RouteLike = Pick<RouteLocationNormalized, 'meta'> & { matched?: ReadonlyArray<unknown> };

export function requirementOf(meta: RouteMeta): PermissionRequirement | null {
  if (meta.permission) return meta.permission;
  if (meta.anyPermission && meta.anyPermission.length) return { anyOf: meta.anyPermission };
  return null;
}

export function isRouteAllowed(route: RouteLike, snapshot: PermissionSnapshot): boolean {
  const meta = route.meta ?? {};
  if (meta.principal && meta.principal !== snapshot.principal) return false;
  const req = requirementOf(meta);
  if (meta.principal === 'staff') {
    if (meta.access === 'self') return true;
    return req ? meets(snapshot, req) : false;
  }
  return req ? meets(snapshot, req) : true;
}

/** True when the hostname is a company subdomain (acme.tasklic.com), where /auth/login is the sign-in page. */
export function isCompanySubdomain(hostname: string): boolean {
  if (!hostname || hostname === 'localhost' || hostname === '127.0.0.1') return false;
  const parts = hostname.split('.');
  return parts.length >= 3 && !['www', 'api', 'admin', 'app'].includes(parts[0]);
}

/** Sign-in route NAME for a principal (staff: the company login on a company subdomain). */
export function signInRouteName(principal: Principal | null | undefined, hostname: string): string {
  if (principal === 'client') return 'client-login';
  if (principal === 'superadmin') return 'superadmin-login';
  return isCompanySubdomain(hostname) ? 'login' : 'signin';
}

/** Home of a principal: staff → their landing page; client / super-admin → their dashboards. */
export function homeRouteFor(snapshot: PermissionSnapshot): RouteLocationRaw {
  return { name: landingRouteName(snapshot) };
}

/** A `?redirect=` value that is safe to follow: an in-app absolute path, not `//host` or a URL. */
export function safeRedirect(value: unknown): string | null {
  const v = Array.isArray(value) ? value[0] : value;
  if (typeof v !== 'string' || !v.startsWith('/') || v.startsWith('//') || v.startsWith('/\\')) return null;
  return v;
}
